import type { EmailOtpType, Session, SupabaseClient } from '@supabase/supabase-js';
import {
  PORTAL_REGISTRY,
  type PortalKey,
} from '@command-center/portal-registry';

export type PasswordEmailAction = 'recovery' | 'invite';

export type EmailAuthRequest =
  | { kind: 'none' }
  | { kind: 'invalid'; action: PasswordEmailAction; portalKey: PortalKey; message: string }
  | { kind: 'legacy'; action: PasswordEmailAction; portalKey: PortalKey }
  | {
      kind: 'token-hash';
      action: PasswordEmailAction;
      portalKey: PortalKey;
      tokenHash: string;
      type: Extract<EmailOtpType, 'recovery' | 'invite'>;
    };

interface BrowserAuthLocation {
  pathname: string;
  search: string;
  hash: string;
}

export type EmailAuthSessionResult =
  | { ok: true; action: PasswordEmailAction; portalKey: PortalKey; session: Session }
  | { ok: false; action: PasswordEmailAction; portalKey: PortalKey; message: string };

/**
 * Parse only the fixed email-auth routes in the production portal registry.
 * Token values are never logged or copied into application state beyond the
 * one verification call.
 */
export function readEmailAuthRequest(location: BrowserAuthLocation): EmailAuthRequest {
  const pathname = normalizePathname(location.pathname);
  const route = Object.values(PORTAL_REGISTRY).flatMap((portal) => [
    { pathname: portal.auth.recoveryPath, action: 'recovery' as const, portalKey: portal.key },
    { pathname: portal.auth.invitePath, action: 'invite' as const, portalKey: portal.key },
  ]).find((candidate) => candidate.pathname === pathname);
  if (!route) {
    const action = pathname.startsWith('/auth/reset/')
      ? 'recovery'
      : pathname.startsWith('/auth/invite/')
        ? 'invite'
        : undefined;
    if (!action) return { kind: 'none' };
    return {
      kind: 'invalid',
      action,
      portalKey: 'oneup',
      message: 'This account link names an unsupported portal. Request a new link from the portal sign-in page.',
    };
  }

  const { action, portalKey } = route;

  const query = new URLSearchParams(location.search);
  const tokenHash = query.get('token_hash')?.trim() ?? '';
  const type = query.get('type')?.trim() ?? '';
  const expectedType = action;

  if (tokenHash || type) {
    if (!tokenHash || type !== expectedType) {
      return { kind: 'invalid', action, portalKey, message: invalidLinkMessage(action) };
    }
    return {
      kind: 'token-hash',
      action,
      portalKey,
      tokenHash,
      type: expectedType,
    };
  }

  // Compatibility for links already sent with Supabase's hosted verify URL.
  // After that endpoint verifies the token, it redirects here with a session
  // in the fragment. New templates use the token-hash branch above instead.
  const fragment = new URLSearchParams(location.hash.replace(/^#/, ''));
  const fragmentType = fragment.get('type');
  if (
    fragment.has('access_token')
    && fragment.has('refresh_token')
    && (!fragmentType || fragmentType === expectedType)
  ) {
    return { kind: 'legacy', action, portalKey };
  }

  return { kind: 'invalid', action, portalKey, message: invalidLinkMessage(action) };
}

/** Establish the session required before the password form is rendered. */
export async function establishEmailAuthSession(
  supabase: SupabaseClient,
  request: Exclude<EmailAuthRequest, { kind: 'none' }>,
): Promise<EmailAuthSessionResult> {
  if (request.kind === 'invalid') {
    return {
      ok: false,
      action: request.action,
      portalKey: request.portalKey,
      message: request.message,
    };
  }

  const result = request.kind === 'token-hash'
    ? await supabase.auth.verifyOtp({ token_hash: request.tokenHash, type: request.type })
    : await supabase.auth.getSession();
  const session = result.data.session;
  if (result.error || !session) {
    return {
      ok: false,
      action: request.action,
      portalKey: request.portalKey,
      message: expiredLinkMessage(request.action),
    };
  }
  return { ok: true, action: request.action, portalKey: request.portalKey, session };
}

export function invalidLinkMessage(action: PasswordEmailAction): string {
  return action === 'recovery'
    ? 'This password reset link is incomplete. Request a new link from the sign-in page.'
    : 'This invitation link is incomplete. Ask One Up to send a new invitation.';
}

function expiredLinkMessage(action: PasswordEmailAction): string {
  return action === 'recovery'
    ? 'This password reset link is invalid or expired. Request a new link from the sign-in page.'
    : 'This invitation link is invalid or expired. Ask One Up to send a new invitation.';
}

function normalizePathname(pathname: string): string {
  if (pathname === '/') return pathname;
  return pathname.replace(/\/+$/, '');
}
